import type { Item, ItemUse } from '../content/types.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { CooldownRecord } from '../domain/encounter.ts';
import { inEncounter } from '../domain/runtime.ts';
import type { HotlistSlot } from './hotlist.ts';
import { inHandCopies } from './inventory.ts';

/**
 * Using a consumable Item: the cadence, and Potion Sickness.
 *
 * RULE DCC-POT-002 - Rules p.49: "In combat, you must spend an Action to
 * 'drink' a potion... Outside of combat, a crawler needs but a free moment."
 *
 * RULE DCC-POT-004 - Rules p.49: "Crawlers cannot consume more than one potion
 * every other round in combat. Attempting to do so will lead to Potion
 * Sickness, which inflicts the Poison Debuff and lasts 15 seconds."
 *
 * PURE. Nothing here writes, rolls or spends; it works out what WOULD happen so
 * the service can commit it and the interface can warn about it.
 */

/**
 * RULE DCC-POT-004, and this number is source-derived rather than assumed.
 *
 * The potion rules never define "every other round". Another Skill in the same
 * ruleset states it as an equation - C&C p.33, Regeneration: "at the start of
 * each round (OR EVERY 10 SECONDS outside of combat)... every other round (OR
 * EVERY 20 SECONDS outside of combat)". With Rules p.17's ten-second round,
 * the book itself equates every-other-round to twenty seconds.
 *
 * Held in SECONDS, not rounds, because that is what the source equates it to
 * and because the existing cooldown machinery counts seconds down through
 * `advanceTime` - so the cadence survives saving and reloading mid-fight
 * without a potion-specific timestamp or round counter.
 */
export const POTION_CADENCE_SECONDS = 20;

/**
 * RULE DCC-POT-004 - Rules p.49: Potion Sickness "inflicts the Poison Debuff
 * and lasts 15 seconds."
 *
 * FIFTEEN SECONDS IS ONE AND A HALF ROUNDS. It cannot be expressed as a number
 * of rounds without distorting it, which is why the existing elapsed-time
 * expiry is used rather than a round count.
 */
export const POTION_SICKNESS_SECONDS = 15;

/** The condition Potion Sickness inflicts. Rules p.49 names the Poison Debuff;
 *  Rules p.45 Table 8 defines it, including its recurring damage and that it
 *  is Stackable. Nothing about that is re-implemented here. */
export const POTION_SICKNESS_CONDITION_ID = 'poisoned';

/** The cooldown record the cadence lives in. Not an Item id: the limit is on
 *  the crawler drinking potions, not on one potion (Rules p.49 says "more than
 *  one potion", not "the same potion twice"). */
export const POTION_CADENCE_SOURCE_ID = 'potion-cadence';

/** Whether an Item can be used at all, and what happens if it is. */
export interface ItemUsePlan {
  /**
   * Whether the use may proceed. See `causesPotionSickness` - a potion drunk
   * too soon is still USABLE.
   */
  canUse: boolean;
  /** Why not, when `canUse` is false. Null otherwise. */
  refusal: string | null;
  /**
   * PROJECT RULING - SOURCE SILENT. True when the cadence has not elapsed, in
   * which case the potion is still consumed, its effect still applies, the
   * Action is still spent, AND the crawler gains Potion Sickness.
   *
   * NOT A REFUSAL, and the distinction is the whole point of this field. The
   * printed rule supplies a CONSEQUENCE for attempting overuse, so refusing the
   * attempt would make Potion Sickness unreachable in ordinary play - a rule
   * that can never fire is not a rule. The books do not say whether the
   * excessive potion still works; this is the project's ruling and is recorded
   * as one in `docs/HOTLIST_AUDIT.md`. It must not be reported as printed.
   */
  causesPotionSickness: boolean;
  /** True when committing this use will spend an Action (Rules p.49). */
  spendsAction: boolean;
  /** Seconds left on the cadence, for the interface to explain itself. */
  cadenceSecondsRemaining: number;
}

/**
 * The live potion cadence, or null.
 *
 * SCOPED TO THE FIGHT IT WAS CREATED IN. Rules p.49 puts the limit "in combat",
 * so a record left over from an earlier encounter must not restrict a potion
 * drunk in the corridor afterwards, and must not poison the crawler at the
 * start of the next fight. The record is only consulted while an encounter is
 * running, and `endEncounter` clears scene-scoped cooldowns, which this is.
 */
export function potionCadence(runtime: RuntimeState): CooldownRecord | null {
  if (!inEncounter(runtime)) return null;
  return runtime.cooldowns.find((c) => c.sourceId === POTION_CADENCE_SOURCE_ID) ?? null;
}

/** Seconds before another potion is free of consequence. Zero outside a fight. */
export function potionCadenceRemaining(runtime: RuntimeState): number {
  const record = potionCadence(runtime);
  return record?.secondsRemaining ?? 0;
}

/**
 * The cadence record a potion use starts.
 *
 * `scope: 'scene'` plus `secondsRemaining`, which together mean exactly
 * "combat-scoped, counted down in seconds" out of primitives that already
 * exist:
 *
 *  - `advanceTime` decrements ANY cooldown carrying `secondsRemaining` and
 *    drops it at zero, so twenty seconds is twenty seconds however the table
 *    gets there - two rounds, or a single 20-second pause out of combat;
 *  - `advanceRound` only clears `scope: 'round'`, so a scene cooldown survives
 *    round boundaries;
 *  - `endEncounter` clears both 'round' and 'scene', so the cadence cannot
 *    follow the crawler out of the fight.
 *
 * **NOT `scope: 'round'`, which was the first attempt and was wrong.**
 * `advanceRound` removes a round-scoped cooldown at the end of the round it
 * started on, `secondsRemaining` or not - so the cadence would have expired
 * after ten seconds and the second potion of a fight would never have been
 * caught. Having `secondsRemaining` on a record is not the same as that field
 * being what ends it.
 */
export function startedPotionCadence(currentRound: number): CooldownRecord {
  return {
    sourceId: POTION_CADENCE_SOURCE_ID,
    scope: 'scene',
    startedOnRound: currentRound,
    secondsRemaining: POTION_CADENCE_SECONDS,
  };
}

export interface ItemUseContext {
  /** Copies in no Gear Slot and no hand: `unallocatedCopies` (DCC-INV-019). */
  /**
   * Copies in NO Gear Slot and NO hand: `quantity - equipped - held`
   * (RULE DCC-INV-019). Built by `physicalUseContext`, not by hand.
   */
  availableCopies: number;
  /**
   * Copies this Item's Hotlist slot claims. Needed because a copy allocated to
   * the Hotlist is not free for the Inventory route to drink.
   */
  hotlistedCopies: number;
  /** True when the copy is being taken from a prepared Hotlist slot. */
  fromHotlist: boolean;
  /**
   * RULE DCC-POT-003 - this use takes a copy already in the crawler's hand
   * (DCC-INV-018), rather than a prepared or an Inventory one.
   */
  fromHand?: boolean;
  /**
   * How many copies are PHYSICALLY IN A HAND right now - `inHandCopies`, which
   * is `equipped` for a held Hands Item and `held` for everything else. Never
   * read straight off `entry.held`.
   */
  inHandCopies?: number;
  /** Total owned, for the three-way allocation check (RULE DCC-INV-019). */
  ownedCopies?: number;
  /** RAW `equipped`, for that check. Not the same question as `inHandCopies`. */
  equippedCopies?: number;
  /** RAW `held` - loosely carried copies only. Also for that check. */
  looseHeldCopies?: number;
  /** Actions the crawler has left, or null outside an encounter. */
  actionsRemaining: number | null;
}

/**
 * What using this Item would do, or why it cannot be done.
 *
 * THE ONE PLACE THE RULES LIVE. The service commits what this decides and the
 * interface displays it; neither re-derives the twenty seconds. That is the
 * same shape E3 settled on for weapon swaps, and for the same reason: a button
 * that offers a use the service will refuse is a button that lies.
 */
export function planItemUse(
  item: Item | undefined,
  runtime: RuntimeState,
  context: ItemUseContext,
): ItemUsePlan {
  const fighting = inEncounter(runtime);
  const cadenceSecondsRemaining = potionCadenceRemaining(runtime);
  const spendsAction = fighting;

  const refuse = (refusal: string): ItemUsePlan => ({
    canUse: false,
    refusal,
    causesPotionSickness: false,
    spendsAction: false,
    cadenceSecondsRemaining,
  });

  if (item === undefined) {
    return refuse('This installation does not have that Item, so it cannot be used.');
  }
  // A malformed definition must not execute even if it reached a catalogue.
  const definitionProblem = itemUseDefinitionProblem(item);
  if (definitionProblem !== null) return refuse(definitionProblem);
  // Absence of `use` is the ORDINARY case, not an error. A Scroll is consumable
  // and has no arm yet; a custom "Ultimate Mana Potion" with nothing authored is
  // inert whatever it is called.
  if (item.use === undefined) {
    return refuse(`${item.name} has no automated use in this application yet.`);
  }
  // THE ALLOCATION INVARIANT, `equipped + held + hotlisted <= owned`, applied to
  // consumption. E2 keeps an over-allocated slot loadable, visible and INERT,
  // and E3 learned that executing one breaks that promise from the other end.
  // Drinking is the same: a copy is a physical object in exactly one place.
  if (context.fromHand === true) {
    // RULE DCC-POT-003 - the copy is already in the hand, so no Hotlist slot
    // has to be fully backed and no unallocated Inventory copy has to exist.
    if ((context.inHandCopies ?? 0) < 1) {
      return refuse(`There is no ${item.name} in the crawler's hands.`);
    }
    // BUT THE TOUCHED ITEM MUST STILL BE WHOLE. RULE DCC-INV-019 - a preserved
    // malformed state such as owned 1 / held 1 / hotlisted 1 is visible and
    // INERT, and drinking the held copy would leave owned 0 with a Hotlist slot
    // still claiming one: the impossible allocation would OUTLIVE the Item that
    // made it impossible. Not repaired here either - shrinking the slot would
    // edit what the player did not ask to change.
    //
    // Narrow on purpose: only THIS Item. An unrelated broken allocation is
    // somebody else's problem and must not block a legal drink.
    // RAW persisted counts here, deliberately: the allocation invariant is
    // about what is stored, and `equipped` + `held` never double-count because
    // an in-hand copy lives in exactly one of them.
    const allocated =
      (context.equippedCopies ?? 0) + (context.looseHeldCopies ?? 0) + context.hotlistedCopies;
    if (allocated > (context.ownedCopies ?? 0)) {
      return refuse(
        `${item.name} is allocated in more places than the crawler owns: ` +
          `${context.equippedCopies ?? 0} worn or wielded, ${context.looseHeldCopies ?? 0} carried and ` +
          `${context.hotlistedCopies} in the Hotlist of ${context.ownedCopies ?? 0}. Sort that out ` +
          'first; drinking one would leave the Hotlist claiming a potion that no longer exists.',
      );
    }
  } else if (context.fromHotlist) {
    // The slot must be fully backed BEFORE anything is drunk. A slot claiming
    // two when the crawler owns one is inert, not "one usable potion".
    if (context.hotlistedCopies > context.availableCopies) {
      return refuse(
        `That Hotlist slot claims ${context.hotlistedCopies} ${item.name} but only ` +
          `${context.availableCopies} ${context.availableCopies === 1 ? 'is' : 'are'} free. ` +
          'Sort the Hotlist out first; a slot that does not match what the crawler carries ' +
          'cannot be used.',
      );
    }
    if (context.hotlistedCopies < 1) {
      return refuse(`There is no prepared ${item.name} to use.`);
    }
  } else {
    // The Inventory route may only take a copy nothing else is relying on.
    // Consuming one that backs a Hotlist slot would leave that slot claiming a
    // potion the crawler no longer has.
    const unallocated = context.availableCopies - context.hotlistedCopies;
    if (unallocated < 1) {
      return refuse(
        context.availableCopies < 1
          ? `There is no ${item.name} free to use.`
          : `Every ${item.name} the crawler has is in the Hotlist. Use the prepared one instead.`,
      );
    }
  }

  // RULE DCC-POT-002 / DCC-HOT-021. In a fight a potion has to have been
  // PREPARED. Rules p.48 lets a Hotlisted potion take effect without appearing
  // in the hand; reaching into the Inventory instead is the Retrieve Action
  // (Rules p.19, Table 3), which is not built. Allowing it here would be a free
  // Retrieve smuggled in through the potion pathway.
  // RULE DCC-POT-002 / DCC-HOT-021. Reaching into the INVENTORY mid-fight is
  // Retrieve. A prepared copy (Rules p.48) and a copy already in the hand
  // (Rules p.37's same-Action restriction implies later Actions are fine) both
  // avoid that, because neither requires reaching anywhere.
  if (fighting && !context.fromHotlist && context.fromHand !== true) {
    return refuse(
      `${item.name} is in the Inventory, not the Hotlist. Reaching into the Inventory mid-fight ` +
        'is the Retrieve Action (Rules p.19), which is not automated yet. Outside a fight it can ' +
        'be used straight from the Inventory.',
    );
  }

  if (fighting && (context.actionsRemaining ?? 0) < 1) {
    return refuse('No Actions left this round. Drinking a potion costs one (Rules p.49).');
  }

  return {
    canUse: true,
    refusal: null,
    // PROJECT RULING - SOURCE SILENT. Too soon is a consequence, not a
    // prohibition. Outside a fight the cadence is not consulted at all.
    causesPotionSickness: fighting && cadenceSecondsRemaining > 0,
    spendsAction,
    cadenceSecondsRemaining,
  };
}

/**
 * What the player asked to do, held across a confirmation dialog.
 *
 * IDENTITY, NOT A CALCULATION. This deliberately carries no next Character, no
 * next Runtime and no `ItemUsePlan`: while a warning is on screen the world can
 * move - another mutation can drink the last potion, the Hotlist slot can be
 * refilled with something else, the encounter can end, the cadence can expire,
 * recovery can become required. Persisting the plan that justified the warning
 * would commit a decision made about a state that no longer exists.
 *
 * `itemId` is carried ALONGSIDE the slot for the same reason E3's `SwapChoice`
 * does: a slot is a position whose contents change, and "whatever is now in
 * slot 3" is not what the player agreed to.
 */
export type ItemUseRequest =
  | { itemId: string; route: 'inventory'; slot: null }
  /** The slot the player was looking at. Checked against what is there NOW. */
  | { itemId: string; route: 'hotlist'; slot: number }
  /**
   * RULE DCC-POT-003 - a copy already IN THE CRAWLER'S HAND (DCC-INV-018).
   *
   * NAMED FOR THE HAND, NOT FOR THE FIELD. It was called `'held'`, which
   * invited the assumption that it meant `InventoryEntry.held` - and that
   * assumption was made, so a custom potion authored as a Hands/Holding Item
   * became undrinkable while sitting in the crawler's fist. The route means
   * "from a physical hand"; WHICH count supplies the copy is
   * `handAllocationField`'s answer, not the route name's.
   *
   * SOURCE-SUPPORTED, not invented. A potion creates its effect when consumed
   * (Rules p.48), drinking costs an Action in combat (Rules p.49), and Retrieve
   * says only that the retrieved Item "cannot be used during your current
   * Action" (Rules p.37) - a restriction that would be pointless if a retrieved
   * consumable could never be used at all. So a copy already in hand may be
   * drunk on a later Action.
   *
   * No slot: a held copy is not backed by a Hotlist entry, and consuming it
   * must not touch one.
   */
  | { itemId: string; route: 'hand'; slot: null };

/**
 * THE PHYSICAL FACTS about one Item and one crawler, built in ONE place.
 *
 * There were three constructions of this context - the Consumables panel, the
 * Play Screen's warning preflight, and the service - and they were allowed to
 * drift. They did: when `held` arrived, two were updated and the warning
 * preflight was not, so a too-soon HELD potion planned as an Inventory use,
 * refused, and skipped the Potion Sickness confirmation the other routes get.
 * The service then ran the real hand route and inflicted the sickness anyway.
 * Both endpoints were right and the wire between them was stale - the same
 * shape as the E3 dropped-argument defect.
 *
 * So the facts are gathered once and every caller asks for them. A fourth route
 * changes this function and nothing else.
 *
 * PURE, and a SNAPSHOT. The authoritative service re-derives it inside the
 * mutation gate; anything built for a screen describes a moment that has passed.
 */
export function physicalUseContext(
  character: {
    id: string;
    inventory: readonly { itemId: string; quantity: number; equipped: number; held: number }[];
    hotlist: readonly (HotlistSlot | undefined)[];
  },
  itemId: string,
  runtime: { encounter?: { actionsRemaining: Record<string, number> } | null },
  fighting: boolean,
  route: ItemUseRequest['route'],
  slot: number | null,
  /** For `inHandCopies`: which count means "in a hand" depends on the Item. */
  item: Item | undefined,
): ItemUseContext {
  const entry = character.inventory.find((e) => e.itemId === itemId);
  const hotlistedCopies = character.hotlist.reduce(
    (total, held) =>
      held !== null && held !== undefined && held.kind === 'item' && held.itemId === itemId
        ? total + held.quantity
        : total,
    0,
  );
  return {
    // RULE DCC-INV-019 - copies in no Gear Slot and no hand, BEFORE Hotlist
    // allocation. Not necessarily spare: a prepared copy is still counted
    // here, which is why `hotlistedCopies` travels alongside it.
    availableCopies: entry === undefined ? 0 : entry.quantity - entry.equipped - entry.held,
    hotlistedCopies,
    ownedCopies: entry?.quantity ?? 0,
    // RAW counts, for the allocation invariant.
    equippedCopies: entry?.equipped ?? 0,
    looseHeldCopies: entry?.held ?? 0,
    fromHotlist: route === 'hotlist' && slot !== null,
    fromHand: route === 'hand',
    // SEMANTIC count, for "is one actually in a hand". Different question, and
    // for a Hands/Holding Item a different field.
    inHandCopies: entry === undefined ? 0 : inHandCopies(entry, item),
    actionsRemaining: fighting ? (runtime.encounter?.actionsRemaining[character.id] ?? 0) : null,
  };
}

/**
 * WHERE the copy would come from. A SOURCE, not a permission.
 *
 * SHARED, so the interface and the service name the same source. In a fight a
 * copy already IN HAND or PREPARED in the Hotlist is legal, and an Inventory
 * one is not, because reaching in is the Retrieve Action (Rules p.37) - but
 * that is `planItemUse`'s ruling to make, and this function must not pretend a
 * prepared copy exists just because an Inventory one would be refused.
 *
 * Outside a fight the Inventory is free - but only a copy nothing else is
 * relying on may be taken from it, so when every copy is allocated to the
 * Hotlist the prepared route is the honest answer rather than a refusal. C&C
 * p.13 puts all five starting Mana Potions in one Hotlist slot, which is
 * exactly this case and must not become "you cannot drink your own potions".
 */
export function chooseUseRoute(input: {
  fighting: boolean;
  hasHotlistSlot: boolean;
  availableCopies: number;
  hotlistedCopies: number;
  /** RULE DCC-INV-018 - copies PHYSICALLY in a hand (`inHandCopies`). */
  inHandCopies: number;
}): ItemUseRequest['route'] {
  // RULE DCC-POT-004 - PROJECT DECISION, NOT A PRINTED RULE. Nothing in the
  // books says which copy a crawler reaches for when they have several. A copy
  // already in the hand is preferred because it is the only one usable in a
  // fight without Retrieve - but it has a visible consequence, since drinking
  // it frees the hand while drinking the prepared copy would not. The
  // Consumables panel therefore SAYS which source it will use rather than
  // surprising the player.
  //
  // THIS FUNCTION SELECTS A SOURCE. IT DOES NOT DECIDE LEGALITY. That is
  // `planItemUse`'s job, and keeping them apart is what stopped this from
  // lying: the combat branch used to return `'hotlist'` unconditionally, so a
  // crawler with an Inventory-only potion and no prepared copy was shown
  // "Use prepared" for a copy that did not exist, while the callback would
  // have sent an Inventory request. The label and the request disagreed.
  //
  // Now it names the source that is actually there. In a fight an Inventory
  // copy is still REFUSED - reaching in is the Retrieve Action - but the
  // refusal explains that, instead of the interface inventing a prepared copy.
  if (input.inHandCopies >= 1) return 'hand';
  if (input.fighting) return input.hasHotlistSlot ? 'hotlist' : 'inventory';
  const unallocated = input.availableCopies - input.hotlistedCopies;
  if (unallocated >= 1) return 'inventory';
  return input.hasHotlistSlot ? 'hotlist' : 'inventory';
}

/**
 * Whether a remembered request still means what it meant.
 *
 * Only the Hotlist route has an identity to lose: an Inventory request names an
 * Item and nothing else, so an unrelated slot changing must never refuse it.
 */
export function requestStillMatches(
  hotlist: readonly (HotlistSlot | undefined)[],
  request: ItemUseRequest,
): boolean {
  // Only the Hotlist route has a SLOT identity to lose. An Inventory or held
  // request names an Item and nothing else, so an unrelated slot changing must
  // never refuse it - the hand route's own staleness check is that the copy is
  // still in hand, which the service re-asks against live state.
  if (request.route !== 'hotlist') return true;
  const entry = hotlist[request.slot] ?? null;
  return entry !== null && entry.kind === 'item' && entry.itemId === request.itemId;
}

/**
 * RULE DCC-POT-001 - `restore-mana-full` is a POTION mechanic.
 *
 * C&C p.13 grounds the only arm there is in the Standard Mana Potion, and Rules
 * p.49's cadence, Action cost and Potion Sickness are all written about
 * potions. An Item that is not one must not acquire them by carrying the arm.
 *
 * The contradiction this prevents is concrete: an Item declaring
 * `consumable: false` would otherwise be DESTROYED by the consumable service,
 * and a suit of armour could trigger Potion Sickness. Checked at every trust
 * boundary - the stored validator, the editor draft, and here - so malformed
 * content reaching a catalogue still cannot execute.
 */
export function itemUseDefinitionProblem(item: {
  name: string;
  kind: string;
  consumable: boolean;
  use?: ItemUse;
}): string | null {
  if (item.use === undefined) return null;
  if (item.use.kind === 'restore-mana-full') {
    if (item.kind !== 'potion' || !item.consumable) {
      return `${item.name} restores Mana when used, which is a Potion effect (C&C p.13), so it must be a consumable Potion.`;
    }
  }
  return null;
}
